import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, FileWarning, ListTodo } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { gbp, fmtDate, KIND_LABELS, STATUS_LABELS } from "@/lib/format";
import { StatusBadge, PageHeader, Empty, DemoBadge } from "@/components/Badges";

const Stat = ({ label, value, testid }) => (
  <div className="card p-5"><p className="caption">{label}</p><p data-testid={testid} className="mt-2 font-display text-2xl font-bold text-[#0B192C]">{value}</p></div>
);

const ListCard = ({ title, icon: Icon, children, testid }) => (
  <section className="card p-6" data-testid={testid}>
    <h2 className="flex items-center gap-2 font-semibold text-[#0B192C]"><Icon className="h-4 w-4 text-[#0D9488]" aria-hidden />{title}</h2>
    <div className="mt-4">{children}</div>
  </section>
);

export default function Dashboard() {
  const { orgId } = useAuth();
  const [d, setD] = useState(null);
  const [bill, setBill] = useState(null);
  useEffect(() => {
    if (orgId) {
      api.get(`/orgs/${orgId}/dashboard`).then(({ data }) => setD(data));
      api.get(`/orgs/${orgId}/billing`).then(({ data }) => setBill(data)).catch(() => {});
    }
  }, [orgId]);
  if (!d) return <p className="text-sm text-slate-500">Loading dashboard…</p>;
  const ipApps = d.applications.filter((a) => a.kind !== "grant");
  const grantApps = d.applications.filter((a) => a.kind === "grant");
  return (
    <div className="fade-up">
      <PageHeader title={d.org.name} sub="Projects, applications, funding and next actions for this workspace.">
        <Link to="/app/projects" data-testid="dashboard-new-project" className="btn-teal btn-sm">New project</Link>
        <Link to="/app/funding" data-testid="dashboard-find-funding" className="btn-ghost btn-sm">Find funding</Link>
      </PageHeader>
      {bill && !bill.access && <Link to="/app/billing" data-testid="dashboard-partnership-banner" className="mb-6 flex items-center justify-between gap-3 rounded-2xl border border-teal-200 bg-teal-50 p-4 text-sm text-teal-900 hover:bg-teal-100"><span><b>Choose your route</b> to prepare applications: platform access for external businesses, or verified Hub membership. No equity involved.</span><ArrowRight className="h-4 w-4 shrink-0" /></Link>}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Projects" value={d.projects.length} testid="stat-projects" />
        <Stat label="IP applications" value={ipApps.length} testid="stat-ip-apps" />
        <Stat label="Funding requested" value={gbp(d.funding_totals.requested)} testid="stat-requested" />
        <Stat label="Funding awarded" value={gbp(d.funding_totals.awarded)} testid="stat-awarded" />
        <Stat label="Funding paid" value={gbp(d.funding_totals.paid)} testid="stat-paid" />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <ListCard title="Recommended next actions" icon={ListTodo} testid="next-actions">
          {d.actions.length ? <ul className="space-y-2">{d.actions.map((a, i) => (
            <li key={i}><Link to={a.link} className="flex items-center justify-between gap-2 rounded px-2 py-1.5 text-sm text-slate-800 hover:bg-slate-50">{a.label}<ArrowRight className="h-4 w-4 shrink-0 text-[#0D9488]" /></Link></li>
          ))}</ul> : <p className="text-sm text-slate-500">Nothing outstanding.</p>}
        </ListCard>
        <ListCard title="Upcoming deadlines" icon={CalendarClock} testid="upcoming-deadlines">
          {d.deadlines.length ? <ul className="divide-y divide-slate-100">{d.deadlines.map((x, i) => (
            <li key={i} className="flex items-center justify-between gap-3 py-2 text-sm"><Link to={x.link} className="hover:underline">{x.label} {x.is_demo && <DemoBadge text="Demo" />}<span className="block text-xs text-slate-500">{x.type}</span></Link><span className="font-mono text-xs">{fmtDate(x.date)}</span></li>
          ))}</ul> : <p className="text-sm text-slate-500">Save funding opportunities to track their deadlines here.</p>}
        </ListCard>
      </div>
      <section className="card mt-6 overflow-x-auto" data-testid="applications-table">
        <div className="flex items-center justify-between p-6 pb-2"><h2 className="font-semibold text-[#0B192C]">Application progress</h2><Link to="/app/applications" className="text-sm text-[#0D9488] hover:underline">All</Link></div>
        {d.applications.length ? (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500"><th className="px-6 py-2">Application</th><th className="px-3">Type</th><th className="px-3">Status</th></tr></thead>
            <tbody>{d.applications.slice(0, 8).map((a) => (
              <tr key={a.id} className="border-t border-slate-100"><td className="px-6 py-3"><Link to={`/app/applications/${a.id}`} className="font-medium hover:underline">{a.title}</Link> {a.is_demo && <DemoBadge text="Demo" />}</td><td className="px-3 text-slate-600">{KIND_LABELS[a.kind]}</td><td className="px-3"><StatusBadge status={a.status} kind={a.kind} /></td></tr>
            ))}</tbody>
          </table>
        ) : <div className="p-6"><Empty title="No applications yet">Create a project, then start an IP or funding application from it.</Empty></div>}
      </section>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <ListCard title="Missing documents" icon={FileWarning} testid="missing-documents">
          {d.missing_documents.length ? <ul className="space-y-1 text-sm">{d.missing_documents.map((m, i) => <li key={i}><Link to={m.link} className="hover:underline">{m.label}</Link> <span className="text-xs text-slate-500">— {m.application}</span></li>)}</ul> : <p className="text-sm text-slate-500">No required documents missing.</p>}
        </ListCard>
        <ListCard title="Saved funding opportunities" icon={CalendarClock} testid="saved-opportunities">
          {d.saved.length ? <ul className="space-y-1 text-sm">{d.saved.map((o) => <li key={o.id}><Link to={`/app/funding/${o.id}`} className="hover:underline">{o.title}</Link> <span className="text-xs text-slate-500">{o.rolling ? "open continuously" : o.close_date ? `closes ${fmtDate(o.close_date)}` : "deadline not published"}</span></li>)}</ul> : <p className="text-sm text-slate-500">None saved yet.</p>}
          <p className="mt-3 text-xs text-slate-500">Grant applications: {grantApps.length} · Statuses tracked: {Object.keys(STATUS_LABELS).length}</p>
        </ListCard>
      </div>
    </div>
  );
}
